import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_AI_POLICY_INFO } from '../../graphql/ai/queries/getAiPolicyInfo.gql';
import { ACCEPT_AI_POLICY } from '../../graphql/ai/mutations/acceptAiPolicy.gql';

export const useAiPolicyAcceptance = () => {
  const { data, loading, refetch, error } = useQuery(GET_AI_POLICY_INFO);
  const [acceptMutation, { loading: accepting }] = useMutation(ACCEPT_AI_POLICY);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const currentVersion = data?.getAiPolicyInfo?.currentVersion;
  const userAcceptedVersion = data?.getAiPolicyInfo?.userAcceptedVersion;
  const policyText = data?.getAiPolicyInfo?.policyText ?? '';
  const needsAcceptance =
    !loading && !!data && (!userAcceptedVersion || userAcceptedVersion !== currentVersion);

  const acceptPolicy = async () => {
    if (!currentVersion) throw new Error('Policy version not loaded');
    setAcceptError(null);
    try {
      const { data: result } = await acceptMutation({ variables: { version: currentVersion } });
      if (!result?.acceptAiPolicy?.success) {
        const message =
          result?.acceptAiPolicy?.error || 'Failed to accept AI policy. Please try again.';
        setAcceptError(message);
        throw new Error(message);
      }
      await refetch();
    } catch (error) {
      if (!acceptError) {
        setAcceptError('Failed to accept AI policy. Please try again.');
      }
      throw error;
    }
  };

  return {
    needsAcceptance,
    policyText,
    currentVersion,
    acceptPolicy,
    accepting,
    acceptError,
    loading,
    error,
    refetch,
    acceptedCurrentPolicy:
      !loading && !error && !!currentVersion && userAcceptedVersion === currentVersion,
  };
};
